import { parseDbTimestamp } from "@repo/shared";
import type {
  ClientCall,
  ClientFile,
  ClientFileDownloadUrlResponse,
  ClientMeeting,
  ClientNote,
} from "@repo/shared/types";
import {
  ChevronDown,
  FileUp,
  Loader2,
  Phone,
  RotateCw,
  Sparkles,
  StickyNote,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { del, get, post } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn, formatBytes } from "@/lib/utils";
import { FileProcessingState } from "./file-processing-state";
import { FileSummaryDialog } from "./file-summary-dialog";
import { NoteEditor } from "./note-editor";
import { T } from "./notes-and-files-section.i18n";
import { type ClientRecordKind, type EditableRecordKind, recordKindIcon } from "./record-kind";
import { TopicEntryEditor } from "./topic-entry-editor";
import { CLIENT_FILE_ACCEPT, useClientFileUpload } from "./use-client-file-upload";

const ENDPOINT_BASE: Record<ClientRecordKind, string> = {
  note: "/internal/client-notes",
  call: "/internal/client-calls",
  meeting: "/internal/client-meetings",
  file: "/internal/client-files",
};

/** One row of the combined table, whichever entity backs it. */
type RecordRow = {
  key: string;
  kind: ClientRecordKind;
  id: string;
  /** ISO timestamp the row is sorted and dated by. */
  date: string;
  topic: string;
  summary: ReactNode;
  by: string;
  file?: ClientFile;
};

type View =
  | { mode: "list" }
  | { mode: "create"; kind: EditableRecordKind }
  | { mode: "edit"; kind: EditableRecordKind; id: string };

export type NotesAndFilesSectionProps = {
  clientId: string;
  notes: ClientNote[];
  calls: ClientCall[];
  meetings: ClientMeeting[];
  files: ClientFile[];
  /** True while any of the lists is still on its first fetch. */
  isLoading: boolean;
  /** Refetch one kind after it changed; resolves once the refetch has settled. */
  onMutate: (kind: ClientRecordKind) => unknown;
  /**
   * Bump to return the section to its list view (clicking the active tab, the
   * Activity tab's Add). A prop rather than a remount key so a running upload
   * and its error banner survive the reset.
   */
  resetSignal?: number;
  /** Open the "new" editor for this kind on mount (Activity tab's Add menu). */
  autoCreate?: EditableRecordKind | null;
  onAutoCreateConsumed?: () => void;
  /** Open this entry's editor on mount (Activity rows, AI widgets, `?tab=…&id=`). */
  autoEdit?: { kind: EditableRecordKind; id: string } | null;
  onAutoEditConsumed?: () => void;
};

// QL-125 — notes, calls, meetings and files in one table with a Type column.
// The list view is the table plus an Add menu; opening a row swaps the whole
// section for that entry's editor (files open in a new tab instead).
export function NotesAndFilesSection({
  clientId,
  notes,
  calls,
  meetings,
  files,
  isLoading,
  onMutate,
  resetSignal = 0,
  autoCreate,
  onAutoCreateConsumed,
  autoEdit,
  onAutoEditConsumed,
}: NotesAndFilesSectionProps) {
  const { formatDate } = useRegionalFormat();
  const t = useT(T);
  const [view, setView] = useState<View>({ mode: "list" });
  const lastReset = useRef(resetSignal);
  if (resetSignal !== lastReset.current) {
    lastReset.current = resetSignal;
    if (view.mode !== "list") setView({ mode: "list" });
  }
  const [isPending, setIsPending] = useState(false);
  // True while a refetch triggered for a not-yet-cached `autoEdit` target runs.
  const [lookupPending, setLookupPending] = useState(false);
  const [summaryFile, setSummaryFile] = useState<ClientFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirm, ConfirmationDialog } = useConfirm();
  const { uploadFiles, isUploading, uploadError, isDragging, dragProps } = useClientFileUpload({
    clientId,
    onUploaded: () => onMutate("file"),
    fallbackError: t.failedUpload,
  });

  useEffect(() => {
    if (autoCreate && view.mode === "list") {
      setView({ mode: "create", kind: autoCreate });
      onAutoCreateConsumed?.();
    }
  }, [autoCreate, view.mode, onAutoCreateConsumed]);

  // A deep link or an Activity row can name an entry that is not in the cached
  // list yet (created in the AI chat or in another tab a moment ago). Refetch
  // that kind and hold the editor until the refetch settles; only then is a
  // still-missing entry treated as gone.
  useEffect(() => {
    if (!autoEdit || (view.mode === "edit" && view.id === autoEdit.id)) return;
    setView({ mode: "edit", kind: autoEdit.kind, id: autoEdit.id });
    onAutoEditConsumed?.();
    const list = autoEdit.kind === "note" ? notes : autoEdit.kind === "call" ? calls : meetings;
    if (!isLoading && !list.some((e) => e.id === autoEdit.id)) {
      setLookupPending(true);
      Promise.resolve(onMutate(autoEdit.kind)).finally(() => setLookupPending(false));
    }
  }, [autoEdit, view, onAutoEditConsumed, notes, calls, meetings, isLoading, onMutate]);

  // Poll while any file is still being transcribed; stop once all are terminal.
  const hasInFlight = files.some(
    (f) => f.processingStatus === "pending" || f.processingStatus === "processing",
  );
  useEffect(() => {
    if (!hasInFlight) return;
    const id = setInterval(() => onMutate("file"), 5000);
    return () => clearInterval(id);
  }, [hasInFlight, onMutate]);

  // One date axis for the whole list: when the thing happened. Calls and
  // meetings carry it explicitly; notes and files are dated by creation, so
  // editing an old note does not float it above yesterday's meeting.
  const rows: RecordRow[] = useMemo(() => {
    const items: RecordRow[] = [];
    for (const n of notes) {
      items.push({
        key: `note-${n.id}`,
        kind: "note",
        id: n.id,
        date: n.createdAt,
        topic: n.title,
        summary: n.summary || n.content || t.noContent,
        by: n.createdBy?.name || "",
      });
    }
    for (const c of calls) {
      items.push({
        key: `call-${c.id}`,
        kind: "call",
        id: c.id,
        date: c.occurredAt,
        topic: c.topic,
        summary: c.summary || c.content || t.noContent,
        by: c.createdBy?.name || "",
      });
    }
    for (const m of meetings) {
      items.push({
        key: `meeting-${m.id}`,
        kind: "meeting",
        id: m.id,
        date: m.occurredAt,
        topic: m.topic,
        summary: m.summary || m.content || t.noContent,
        by: m.createdBy?.name || "",
      });
    }
    for (const f of files) {
      items.push({
        key: `file-${f.id}`,
        kind: "file",
        id: f.id,
        date: f.createdAt,
        topic: f.fileName,
        summary: <FileProcessingState file={f} fallback={formatBytes(f.size)} />,
        by: f.uploadedBy?.name || "",
        file: f,
      });
    }
    return items.sort((a, b) => b.date.localeCompare(a.date));
  }, [notes, calls, meetings, files, t]);

  const backToList = (kind: ClientRecordKind) => {
    setView({ mode: "list" });
    onMutate(kind);
  };

  const deleteRecord = async (kind: ClientRecordKind, id: string) => {
    setIsPending(true);
    try {
      await del(`${ENDPOINT_BASE[kind]}/${clientId}/${id}`);
      setView({ mode: "list" });
      onMutate(kind);
    } catch (err) {
      log.error({ err, clientId, kind, id }, "Failed to delete client record");
    } finally {
      setIsPending(false);
    }
  };

  const confirmDelete = async (row: RecordRow) => {
    const confirmed = await confirm({
      title: t.deleteTitle,
      description: t.deleteConfirmation(row.topic),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (confirmed) await deleteRecord(row.kind, row.id);
  };

  const openFile = async (file: ClientFile) => {
    // Open the tab synchronously, inside the click's user gesture, and point it
    // at the signed URL once it arrives: a window.open after the awaited fetch
    // is popup-blocked in Safari and in strict Chrome profiles.
    const win = window.open("about:blank", "_blank");
    if (win) win.opener = null;
    try {
      const { url } = await get<ClientFileDownloadUrlResponse>(
        `${ENDPOINT_BASE.file}/${clientId}/${file.id}/url`,
      );
      if (win) win.location.replace(url);
      else window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      win?.close();
      log.error({ err, clientId, fileId: file.id }, "Failed to open client file");
    }
  };

  const reprocessFile = async (file: ClientFile) => {
    try {
      await post(`${ENDPOINT_BASE.file}/${clientId}/${file.id}/reprocess`);
      onMutate("file");
    } catch (err) {
      log.error({ err, clientId, fileId: file.id }, "Failed to reprocess client file");
    }
  };

  // The entry being edited, once its list has arrived. A deep link
  // (`?tab=meetings&id=…`) opens the editor before the query resolves; the
  // editor seeds its form state on mount, so it must not mount on `null` and
  // then quietly turn into a "new entry" form.
  const editingEntry: ClientNote | ClientCall | ClientMeeting | null = useMemo(() => {
    if (view.mode !== "edit") return null;
    const list = view.kind === "note" ? notes : view.kind === "call" ? calls : meetings;
    return list.find((e) => e.id === view.id) ?? null;
  }, [view, notes, calls, meetings]);
  const editingMissing = view.mode === "edit" && !editingEntry && !isLoading && !lookupPending;
  useEffect(() => {
    // Deleted elsewhere (or a stale link): fall back to the list.
    if (editingMissing) setView({ mode: "list" });
  }, [editingMissing]);

  const openRow = (row: RecordRow) => {
    if (row.kind === "file") {
      if (row.file) void openFile(row.file);
      return;
    }
    setView({ mode: "edit", kind: row.kind, id: row.id });
  };

  if (view.mode === "edit" && !editingEntry) {
    return (
      <div className="flex justify-center py-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (view.mode !== "list") {
    const editorKey = editingEntry?.id ?? "new";
    if (view.kind === "note") {
      return (
        <NoteEditor
          key={editorKey}
          note={editingEntry as ClientNote | null}
          clientId={clientId}
          onDelete={(id) => deleteRecord("note", id)}
          onCancel={() => backToList("note")}
        />
      );
    }
    if (view.kind === "call") {
      return (
        <TopicEntryEditor<ClientCall>
          key={editorKey}
          entry={editingEntry as ClientCall | null}
          endpointBase={`${ENDPOINT_BASE.call}/${clientId}`}
          onDelete={(id) => deleteRecord("call", id)}
          onCancel={() => backToList("call")}
          entryWord={t.callWord}
        />
      );
    }
    return (
      <TopicEntryEditor<ClientMeeting>
        key={editorKey}
        entry={editingEntry as ClientMeeting | null}
        endpointBase={`${ENDPOINT_BASE.meeting}/${clientId}`}
        onDelete={(id) => deleteRecord("meeting", id)}
        onCancel={() => backToList("meeting")}
        entryWord={t.meetingWord}
        meetingRoom
      />
    );
  }

  const kindLabel: Record<ClientRecordKind, string> = {
    note: t.note,
    call: t.call,
    meeting: t.meeting,
    file: t.file,
  };

  return (
    <>
      {/* The whole list is the drop target so a file can land anywhere on the tab. */}
      <div
        className={cn(
          "relative space-y-4 rounded-lg transition-colors",
          isDragging && "ring-2 ring-primary bg-primary/5",
        )}
        {...dragProps}
      >
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-primary/10 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2 text-primary">
              <FileUp className="h-10 w-10" />
              <p className="font-medium">{t.dropHere}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <input
            ref={fileInputRef}
            type="file"
            accept={CLIENT_FILE_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              e.target.value = "";
              void uploadFiles(picked);
            }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button loading={isUploading}>
                {t.add}
                <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setView({ mode: "create", kind: "note" })}>
                <StickyNote className="h-4 w-4 mr-2" />
                {t.addNote}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setView({ mode: "create", kind: "call" })}>
                <Phone className="h-4 w-4 mr-2" />
                {t.addCall}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setView({ mode: "create", kind: "meeting" })}>
                <Users className="h-4 w-4 mr-2" />
                {t.addMeeting}
              </DropdownMenuItem>
              {/* Only the upload entry waits for a running upload; notes, calls
                  and meetings stay reachable meanwhile. */}
              <DropdownMenuItem
                disabled={isUploading}
                onSelect={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                {isUploading ? t.uploading : t.uploadFile}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {uploadError && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md whitespace-pre-line">
            {uploadError}
          </div>
        )}

        {rows.length === 0 && isLoading ? (
          <div className="flex justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState>{t.none}</EmptyState>
        ) : (
          <Table variant="interactive">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">{t.columnDate}</TableHead>
                <TableHead className="w-[110px]">{t.columnType}</TableHead>
                <TableHead className="w-[260px]">{t.columnTopic}</TableHead>
                <TableHead>{t.columnSummary}</TableHead>
                <TableHead className="w-[160px]">{t.columnBy}</TableHead>
                <TableActionsHead>{t.columnActions}</TableActionsHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} onClick={() => openRow(row)}>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {formatDate(parseDbTimestamp(row.date))}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      {recordKindIcon(row.kind, row.file)}
                      {kindLabel[row.kind]}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium truncate max-w-[260px]">{row.topic}</TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-0">
                    <span className="block truncate">{row.summary}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-[160px]">
                    {row.by}
                  </TableCell>
                  <TableActionsCell>
                    {row.file?.processingStatus === "done" && row.file.summary && (
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.showSummary}
                        onClick={() => setSummaryFile(row.file ?? null)}
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    )}
                    {row.file?.processingStatus === "failed" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.retry}
                        onClick={() => row.file && reprocessFile(row.file)}
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      tooltip={t.delete}
                      className="text-destructive hover:text-destructive"
                      disabled={isPending}
                      onClick={() => confirmDelete(row)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ConfirmationDialog />
      <FileSummaryDialog file={summaryFile} onClose={() => setSummaryFile(null)} />
    </>
  );
}
